import { useCallback, useMemo } from 'react';
import { history, useLocation } from 'umi';
import {
  buildEmailCenterSearch,
  EmailCenterUrlState,
  parseEmailCenterSearch,
} from './urlState';

/**
 * URL-backed state for the Touchpoints pages built on the email stack. Tab
 * switches — including the Design page's sections — and template opens push a
 * history entry (back returns to the previous tab or section, or closes the
 * editor); journey tweaks and template closes replace, so they don't pollute
 * history.
 */
export function useEmailCenterUrlState(): [
  EmailCenterUrlState,
  (patch: Partial<EmailCenterUrlState>) => void,
] {
  const location = useLocation();

  const urlState = useMemo(
    () => parseEmailCenterSearch(location.search),
    [location.search],
  );

  const setUrlState = useCallback(
    (patch: Partial<EmailCenterUrlState>) => {
      const current = parseEmailCenterSearch(location.search);
      const next = { ...current, ...patch };
      const search = buildEmailCenterSearch(next);
      const to = { pathname: location.pathname, search };
      const switchesTab = !!patch.tab && patch.tab !== current.tab;
      // A Design sub-tab is a tab: Back should return to the section you left.
      const switchesSection =
        !!patch.themeSection && patch.themeSection !== current.themeSection;
      const opensTemplate =
        patch.templateId != null && patch.templateId !== current.templateId;
      if (switchesTab || switchesSection || opensTemplate) {
        history.push(to);
      } else {
        history.replace(to);
      }
    },
    [location.pathname, location.search],
  );

  return [urlState, setUrlState];
}
